import {DialogDisclosure, DialogDismiss, DialogHeading, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Pencil01Icon} from '@/components/ui/icon/Icon'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {AgendaEditEventFormProvider} from '@/features/agenda/components/agenda-event-form/agenda-edit-event-form-provider/AgendaEditEventFormProvider'
import {AgendaEventFormFields} from '@/features/agenda/components/agenda-event-form/agenda-event-form-fields/AgendaEventFormFields'
import {
    StyledAgendaEditEventButton,
    StyledEventFormDialog,
    StyledEventFormDialogBody,
    StyledEventFormDialogHeader
} from '@/features/agenda/components/edit-event-form-dialog/style'
import {EditEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {useTranslation} from '@/translations/i18n'
import {useDialogStore} from '@ariakit/react'
import {ComponentType, useState} from 'react'
import {EditEventFormDialogFooter} from './EditEventFormDialogFooter'

export const EditEventFormDialog = ({
    customTrigger: CustomTrigger,
    isDisabled,
    defaultValues,
    isRecurringEvent,
    isExternalStructure
}: {
    customTrigger?: ComponentType<{onClick: () => void}>
    defaultValues: EditEventFormValidationSchema
    isDisabled?: boolean
    isRecurringEvent?: boolean
    /** Renders the reduced form used for events booked on an external structure */
    isExternalStructure?: boolean
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    CustomTrigger ? (
                        <CustomTrigger onClick={() => setIsOpen(true)} />
                    ) : (
                        <StyledAgendaEditEventButton variant="ghost" disabled={isDisabled}>
                            <Pencil01Icon />
                            {t('commons:edit')}
                        </StyledAgendaEditEventButton>
                    )
                }
            />
            <StyledEventFormDialog width="780px" overflowY="unset">
                <EditEventFormDialogContent
                    defaultValues={defaultValues}
                    isRecurringEvent={isRecurringEvent}
                    isExternalStructure={isExternalStructure}
                />
            </StyledEventFormDialog>
        </DialogProvider>
    )
}

const EditEventFormDialogContent = ({
    defaultValues,
    isRecurringEvent,
    isExternalStructure
}: {
    defaultValues: EditEventFormValidationSchema
    isRecurringEvent?: boolean
    isExternalStructure?: boolean
}) => {
    const {t} = useTranslation()
    return (
        <>
            <StyledEventFormDialogHeader>
                <DialogHeading>
                    {isExternalStructure ? t('agenda:edit_external_structure_event') : t('agenda:edit_event')}
                </DialogHeading>
                <DialogDismiss />
            </StyledEventFormDialogHeader>
            <AgendaEditEventFormProvider defaultValues={defaultValues}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                    <StyledEventFormDialogBody>
                        <AgendaEventFormFields
                            mode="edit"
                            defaultDate={defaultValues.date?.date}
                            isExternalStructure={isExternalStructure}
                        />
                    </StyledEventFormDialogBody>
                </ScrollArea>
                <EditEventFormDialogFooter
                    eventStatus={defaultValues.status || 'scheduled'}
                    isRecurringEvent={isRecurringEvent}
                />
            </AgendaEditEventFormProvider>
        </>
    )
}
